import {
  createSelector,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";

import type { ModelConfig } from "@/libs/models";
import type { RootState } from "../../app/store";

export type GroundTruth = {
  lat: number;
  lng: number;
  floorIdentifier: number;
  buildingIdentifier: number;
  isIndoor?: boolean;
};

// TODO floor and building identifier repeated for each calibration and each GT
export type Calibration = {
  filename: string;
  buildingIdentifier: number;
  floorIdentifier: number;
  size?: number;
  gts: GroundTruth[];
};

interface SliceProps {
  modelConfig?: ModelConfig;
  newCalibration: {
    gts: GroundTruth[];
    showDialog: boolean;
  };
  localCalibrations: Calibration[];
  remoteCalibrations: Calibration[];
}

const initialState: SliceProps = {
  localCalibrations: [],
  modelConfig: undefined,
  newCalibration: {
    gts: [],
    showDialog: false,
  },
  remoteCalibrations: [],
};

const CalibrationSlice = createSlice({
  initialState,
  name: "calibration",
  reducers: {
    addNewCalibrationGt: (
      state: SliceProps,
      action: PayloadAction<GroundTruth>,
    ) => {
      const newGts = state.newCalibration.gts;
      newGts.push(action.payload);
      state.newCalibration.gts = [...newGts];
    },
    resetNewCalibrationGts: (state: SliceProps) => {
      state.newCalibration.gts = [];
    },
    setLocalCalibrations: (
      state: SliceProps,
      action: PayloadAction<Calibration[]>,
    ) => {
      state.localCalibrations = action.payload;
    },
    setModelConfig: (
      state: SliceProps,
      action: PayloadAction<ModelConfig | undefined>,
    ) => {
      state.modelConfig = action.payload;
    },
    setNewCalibrationShowDialog: (
      state: SliceProps,
      action: PayloadAction<boolean>,
    ) => {
      state.newCalibration.showDialog = action.payload;
    },
    setRemoteCalibrations: (
      state: SliceProps,
      action: PayloadAction<Calibration[]>,
    ) => {
      state.remoteCalibrations = action.payload;
    },
  },
});

// Selectors
const sliceData = (state: RootState) => state.calibration;

export const selectNewCalibrationGts = createSelector(
  sliceData,
  (item) => item.newCalibration.gts,
);

export const selectNewCalibrationShowDialog = createSelector(
  sliceData,
  (item) => item.newCalibration.showDialog,
);

export const selectIsCalibrating = createSelector(
  sliceData,
  (item) => item.newCalibration.gts.length > 0,
);

export const selectLocalCalibrations = createSelector(
  sliceData,
  (item) => item.localCalibrations,
);

export const selectRemoteCalibrations = createSelector(
  sliceData,
  (item) => item.remoteCalibrations,
);

export const selectModelConfig = createSelector(
  sliceData,
  (item) => item.modelConfig,
);

// Action creators are generated for each case reducer function
export const {
  resetNewCalibrationGts,
  addNewCalibrationGt,
  setNewCalibrationShowDialog,
  setLocalCalibrations,
  setRemoteCalibrations,
  setModelConfig,
} = CalibrationSlice.actions;

export default CalibrationSlice.reducer;
